import { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { getEarnedAchievements, ELITE_REFLEX_MS } from '../achievements';

// Statistics: running aggregates kept in refs (no re-render on every tap) and
// flushed to Firestore periodically. Snapshotted into `statsView` when the
// Stats screen opens.
//
// - `currentPageRef`: always-current page, so the active-time clock only runs
//   while the orbs are actually on screen.
// - `demoStepRef`: always-current demo step — taps and time during the
//   first-launch demo must never land in the user's stats.
//
// Below this, an "appear→tap" gap isn't a genuine reaction — it's a
// pre-emptive/rhythmic tap that beat the orb's actual appearance (easy to
// fall into once you're in the swing of it, especially at fast reappear
// speeds). Excluded so "best response" can never show a physically
// implausible time faster than a real human reaction. 100ms is the accepted
// "impossible" line (it's literally the false-start threshold in Olympic
// sprinting — a reaction that fast can't have actually processed the
// stimulus); 110ms leaves a thin buffer above it.
const MIN_PLAUSIBLE_REACTION_MS = 110;

const emptyStats = () => ({
  totalTaps: 0,
  totalSessions: 0,
  totalActiveMs: 0,      // time spent on the orb screen ("calm time")
  totalReactionMs: 0,    // sum of appear→tap times
  reactionCount: 0,
  bestReactionMs: null,  // fastest appear→tap
  eliteReflexCount: 0,      // cumulative taps at/under ELITE_REFLEX_MS
  eliteReflexStreak: 0,     // current consecutive run of them (any slower plausible tap resets it)
  bestEliteReflexStreak: 0, // longest such run ever
  lastSessionAt: null,
  activityDays: [],       // local YYYY-MM-DD keys; enough for gentle rhythms, not a detailed log
  completedRoutines: 0,
  completedRoutineIds: [],
  checkInsCompleted: 0,   // optional "how do you feel now?" answered after a routine
  improvedCheckIns: 0,    // of those, how many said they felt calmer
});

export const localDayKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const dayNumber = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
};

export const calculateRhythms = (activityDays = [], today = new Date()) => {
  const days = [...new Set(activityDays)].sort().map(dayNumber);
  if (!days.length) return { currentStreakDays: 0, longestStreakDays: 0 };
  let longest = 1;
  let run = 1;
  for (let i = 1; i < days.length; i += 1) {
    run = days[i] === days[i - 1] + 1 ? run + 1 : 1;
    longest = Math.max(longest, run);
  }
  const todayNum = dayNumber(localDayKey(today));
  const last = days[days.length - 1];
  let current = 0;
  if (last === todayNum || last === todayNum - 1) {
    current = 1;
    for (let i = days.length - 1; i > 0 && days[i] === days[i - 1] + 1; i -= 1) current += 1;
  }
  return { currentStreakDays: current, longestStreakDays: longest };
};

// Session-scoped tallies (since the current orb session began), shown
// alongside lifetime and resettable on their own.
const emptySessionStats = () => ({
  taps: 0,
  activeMs: 0,
  reactionMs: 0,
  reactionCount: 0,
  bestReactionMs: null,
});

const useStats = ({ currentPageRef, demoStepRef }) => {
  const statsRef = useRef(emptyStats());
  const statsOwnerRef = useRef(null);
  const hydratedUidRef = useRef(null);
  const sessionStatsRef = useRef(emptySessionStats());
  const [statsView, setStatsView] = useState(null); // { lifetime, session } → Stats screen open
  const orb1AppearedAtRef = useRef(null); // performance.now() when each orb last appeared
  const orb2AppearedAtRef = useRef(null);
  const sessionStartRef = useRef(null);   // Date.now() the current orb session began
  const tapsSinceFlushRef = useRef(0);
  // Achievement ids already earned — seeded from Firestore on login so a
  // returning user is never "celebrated" for milestones they passed long ago.
  const earnedIdsRef = useRef(new Set());
  const [newAchievement, setNewAchievement] = useState(null); // the badge to toast, if any
  const dismissNewAchievement = useCallback(() => setNewAchievement(null), []);

  // The same metric shape used everywhere achievements are evaluated (Stats
  // screen, here) — derives the two values that aren't stored directly.
  const currentMetrics = useCallback(() => {
    const life = statsRef.current;
    const rhythms = calculateRhythms(life.activityDays);
    return {
      ...life,
      ...rhythms,
      distinctUseDays: life.activityDays.length,
      uniqueRoutinesCompleted: (life.completedRoutineIds || []).length,
    };
  }, []);

  // Call after anything that could cross a milestone. Cheap (linear scan over
  // ~30 badges) so it's fine to call liberally rather than trying to target
  // only the metric that just changed.
  const checkForNewAchievements = useCallback(() => {
    const earned = getEarnedAchievements(currentMetrics());
    const newlyEarned = earned.filter(({ id }) => !earnedIdsRef.current.has(id));
    if (!newlyEarned.length) return;
    earned.forEach(({ id }) => earnedIdsRef.current.add(id));
    // Several tiers can land in the same instant (e.g. a long first-ever
    // session) — show the last one reached rather than stacking toasts.
    setNewAchievement(newlyEarned[newlyEarned.length - 1]);
  }, [currentMetrics]);

  // Bank the active time accrued since the session/segment began, then pause it.
  const bankActive = useCallback(() => {
    if (sessionStartRef.current != null) {
      const elapsed = Date.now() - sessionStartRef.current;
      statsRef.current.totalActiveMs += elapsed;
      sessionStatsRef.current.activeMs += elapsed;
      sessionStartRef.current = null;
      checkForNewAchievements();
    }
  }, [checkForNewAchievements]);

  // Resume active-time accrual, but only while the orbs are actually on screen
  // and not during the demo (the demo must never count toward stats).
  const resumeActiveIfMain = useCallback(() => {
    if (currentPageRef.current === 'main' && sessionStartRef.current == null && demoStepRef.current < 0) {
      sessionStartRef.current = Date.now();
    }
  }, [currentPageRef, demoStepRef]);

  // Persist the running aggregates (merge, single doc per user — last write wins).
  const flushStats = useCallback(async () => {
    const u = auth.currentUser;
    if (!u || hydratedUidRef.current !== u.uid) return;
    try {
      await setDoc(doc(db, 'users', u.uid), { stats: statsRef.current }, { merge: true });
    } catch (e) {
      console.warn('Could not persist stats:', e?.message);
    }
  }, []);

  // Open the Stats screen with a fresh snapshot (and bank/flush current time).
  const openStats = useCallback(() => {
    bankActive();
    resumeActiveIfMain();
    const life = { ...statsRef.current };
    const rhythms = calculateRhythms(life.activityDays);
    setStatsView({
      lifetime: { ...life, ...rhythms, distinctUseDays: life.activityDays.length },
      session: { ...sessionStatsRef.current },
    });
    flushStats();
  }, [bankActive, resumeActiveIfMain, flushStats]);

  // Begin counting a real session (taps, time, reaction). Called when the orb
  // screen opens — but for a first-ever launch, only AFTER the demo finishes, so
  // the demo's own taps/time never land in the stats.
  const startStatsSession = useCallback(() => {
    statsRef.current.totalSessions += 1;
    statsRef.current.lastSessionAt = new Date().toISOString();
    const today = localDayKey();
    if (!statsRef.current.activityDays.includes(today)) {
      // Keep a bounded history (ten years of daily keys) without recording
      // session contents or individual tap timestamps.
      statsRef.current.activityDays = [...statsRef.current.activityDays, today].slice(-3650);
    }
    sessionStatsRef.current = emptySessionStats();
    sessionStartRef.current = Date.now();
    const t = performance.now();
    orb1AppearedAtRef.current = t;
    orb2AppearedAtRef.current = t;
    checkForNewAchievements();
  }, [checkForNewAchievements]);

  // Reset the active-time baseline so no time accrued before a reset gets banked.
  const rebaseActive = useCallback(() => {
    sessionStartRef.current = currentPageRef.current === 'main' ? Date.now() : null;
  }, [currentPageRef]);

  const resetSessionStats = useCallback(() => {
    sessionStatsRef.current = emptySessionStats();
    rebaseActive();
    setStatsView((v) => (v ? { ...v, session: { ...sessionStatsRef.current } } : v));
  }, [rebaseActive]);

  const resetLifetimeStats = useCallback(() => {
    if (!window.confirm('Reset all-time stats? This clears your totals for good.')) return;
    statsRef.current = emptyStats();
    sessionStatsRef.current = emptySessionStats();
    rebaseActive();
    flushStats();
    setStatsView((v) => (v ? { lifetime: { ...statsRef.current }, session: { ...sessionStatsRef.current } } : v));
  }, [rebaseActive, flushStats]);

  // The orb reappeared: start the next reaction clock for that side.
  const markOrbAppeared = useCallback((orbNumber) => {
    const ref = orbNumber === 1 ? orb1AppearedAtRef : orb2AppearedAtRef;
    ref.current = performance.now();
  }, []);

  // Count a tap and time it from when that orb last appeared. Skipped entirely
  // during the demo so it never lands in the user's stats.
  const recordTap = useCallback((orbNumber) => {
    if (demoStepRef.current >= 0) return;
    const appearedRef = orbNumber === 1 ? orb1AppearedAtRef : orb2AppearedAtRef;
    const life = statsRef.current;
    const sess = sessionStatsRef.current;
    life.totalTaps += 1;
    sess.taps += 1;
    if (appearedRef.current != null) {
      const reaction = performance.now() - appearedRef.current;
      // Ignore idle gaps (walked away) and impossibly fast "reactions"
      // (pre-emptive taps): only count plausible ones.
      if (reaction >= MIN_PLAUSIBLE_REACTION_MS && reaction < 10000) {
        life.totalReactionMs += reaction;
        life.reactionCount += 1;
        life.bestReactionMs = life.bestReactionMs == null ? reaction : Math.min(life.bestReactionMs, reaction);
        sess.reactionMs += reaction;
        sess.reactionCount += 1;
        sess.bestReactionMs = sess.bestReactionMs == null ? reaction : Math.min(sess.bestReactionMs, reaction);

        // Elite-speed streak: any plausible-but-slower tap resets it, so it
        // only ever grows on genuinely consecutive elite taps.
        if (reaction <= ELITE_REFLEX_MS) {
          life.eliteReflexCount += 1;
          life.eliteReflexStreak += 1;
          life.bestEliteReflexStreak = Math.max(life.bestEliteReflexStreak, life.eliteReflexStreak);
        } else {
          life.eliteReflexStreak = 0;
        }
      }
      appearedRef.current = null; // consumed until the orb reappears
    }
    tapsSinceFlushRef.current += 1;
    if (tapsSinceFlushRef.current >= 25) { tapsSinceFlushRef.current = 0; flushStats(); }
    checkForNewAchievements();
  }, [demoStepRef, flushStats, checkForNewAchievements]);

  const completeRoutine = useCallback((routineId) => {
    if (!routineId) return;
    const life = statsRef.current;
    life.completedRoutines = Number(life.completedRoutines || 0) + 1;
    life.completedRoutineIds = [...new Set([...(life.completedRoutineIds || []), routineId])].slice(-50);
    flushStats();
    checkForNewAchievements();
  }, [flushStats, checkForNewAchievements]);

  // An entirely optional "how do you feel now?" after a routine. `feeling` is
  // 'calmer' | 'same' | 'less' — only 'calmer' counts toward the improvement
  // milestone. Never required to close the completion screen.
  const recordCheckIn = useCallback((feeling) => {
    const life = statsRef.current;
    life.checkInsCompleted = Number(life.checkInsCompleted || 0) + 1;
    if (feeling === 'calmer') life.improvedCheckIns = Number(life.improvedCheckIns || 0) + 1;
    flushStats();
    checkForNewAchievements();
  }, [flushStats, checkForNewAchievements]);

  // Seed the lifetime aggregates from the user's Firestore doc on login, and
  // mark whatever they've already earned so none of it re-celebrates.
  const loadStats = useCallback((data, uid = auth.currentUser?.uid) => {
    if (!uid || uid !== statsOwnerRef.current || hydratedUidRef.current === uid) return;
    const pending = statsRef.current;
    const loaded = { ...emptyStats(), ...(data || {}) };
    // Defensively normalize older documents as well as enforcing new writes in rules.
    loaded.activityDays = Array.isArray(loaded.activityDays) ? loaded.activityDays.filter(day => typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day)) : [];
    loaded.completedRoutineIds = Array.isArray(loaded.completedRoutineIds) ? loaded.completedRoutineIds.filter(id => typeof id === 'string') : [];
    for (const key of ['totalTaps', 'totalSessions', 'totalActiveMs', 'totalReactionMs', 'reactionCount', 'eliteReflexCount', 'completedRoutines', 'checkInsCompleted', 'improvedCheckIns']) {
      loaded[key] = (Number.isFinite(loaded[key]) ? loaded[key] : 0) + pending[key];
    }
    if (pending.bestReactionMs != null) loaded.bestReactionMs = loaded.bestReactionMs == null ? pending.bestReactionMs : Math.min(loaded.bestReactionMs, pending.bestReactionMs);
    if (pending.totalTaps) loaded.eliteReflexStreak = pending.eliteReflexStreak;
    loaded.bestEliteReflexStreak = Math.max(loaded.bestEliteReflexStreak || 0, pending.bestEliteReflexStreak);
    loaded.lastSessionAt = pending.lastSessionAt || loaded.lastSessionAt;
    loaded.activityDays = [...new Set([...loaded.activityDays, ...pending.activityDays])].sort().slice(-3650);
    loaded.completedRoutineIds = [...new Set([...loaded.completedRoutineIds, ...pending.completedRoutineIds])].slice(-50);
    statsRef.current = loaded;
    hydratedUidRef.current = uid;
    earnedIdsRef.current = new Set(getEarnedAchievements(currentMetrics()).map(({ id }) => id));
    if (pending.totalSessions || pending.totalTaps || pending.completedRoutines) flushStats();
  }, [currentMetrics, flushStats]);

  // Wipe in-memory stats so the next account starts clean (flush before logout).
  const wipeStats = useCallback(() => {
    statsOwnerRef.current = null;
    hydratedUidRef.current = null;
    statsRef.current = emptyStats();
    sessionStatsRef.current = emptySessionStats();
    sessionStartRef.current = null;
    earnedIdsRef.current = new Set();
    setNewAchievement(null);
    setStatsView(null);
  }, []);

  const beginStatsLoad = useCallback((uid) => {
    wipeStats();
    statsOwnerRef.current = uid;
  }, [wipeStats]);

  // Flush (and pause the active clock) when the app is backgrounded; resume on
  // return. iOS fires these on swipe-away / app-switcher — our best save point.
  useEffect(() => {
    const onHide = () => { bankActive(); flushStats(); };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') onHide();
      else resumeActiveIfMain();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
    };
  }, [bankActive, flushStats, resumeActiveIfMain]);

  return {
    statsView, setStatsView,
    bankActive, resumeActiveIfMain, flushStats, openStats, startStatsSession,
    resetSessionStats, resetLifetimeStats,
    recordTap, markOrbAppeared, completeRoutine, recordCheckIn,
    loadStats, beginStatsLoad, wipeStats,
    newAchievement, dismissNewAchievement,
  };
};

export default useStats;
